import { Button, ButtonText, Text, View } from '@gluestack-ui/themed'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { Linking, StyleSheet } from 'react-native'
import Ionicons from 'react-native-vector-icons/Ionicons'
import { useSelector } from 'react-redux'
import { NotificationApi } from '../../api/Notification/NotificationApi'
import { NotificationCategorie } from '../../api/Notification/types'
import { i18n } from '../../lib/i18n'
import { themeSelector } from '../../redux/theme/selector'
import { requestNotificationPermission, startNotifications } from '../../services/notification'
import { addOpacity } from '../../services/theme'
import { theme } from '../../theme'
import { ModalBottom } from '../ModalBottom'

const ICON_CIRCLE_SIZE = 48
const ICON_SIZE = 24
const CHIP_RADIUS = 999
const BUTTON_RADIUS = 10
const MAX_CHIPS = 3

export interface NotificationPromptSheetProps {
  show: boolean
  onClose: () => void
}

export const NotificationPromptSheet: FunctionComponent<NotificationPromptSheetProps> = ({ show, onClose }) => {
  const clientTheme = useSelector(themeSelector)
  const [categories, setCategories] = useState<NotificationCategorie[]>([])

  const primaryColor = clientTheme?.brandPrimary || theme.colors.blue

  const loadCategories = async () => {
    try {
      setCategories((await NotificationApi.loadCategories()).slice(0, MAX_CHIPS))
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (show) {
      loadCategories()
    }
  }, [show])

  const handleEnable = async () => {
    const permission = await requestNotificationPermission()
    if (permission === 'granted') {
      startNotifications()
    }
    if (permission === 'blocked') {
      Linking.openSettings()
    }
    onClose()
  }

  return (
    <ModalBottom show={show} onClose={onClose}>
      <View style={styles.iconCircle}>
        <Ionicons name="notifications-outline" size={ICON_SIZE} color={theme.colors.black} />
      </View>
      <Text style={styles.title}>{i18n.t('launchScreens.notifications.title')}</Text>
      <Text style={styles.subtitle}>{i18n.t('launchScreens.notifications.subtitle')}</Text>
      {categories.length > 0 && (
        <View style={styles.chips}>
          {categories.map(({ id, title }) => (
            <View key={id} style={[styles.chip, { backgroundColor: addOpacity(primaryColor, theme.opacity) }]}>
              <Text style={[styles.chipText, { color: primaryColor }]}>{title}</Text>
            </View>
          ))}
        </View>
      )}
      <Button style={[styles.button, { backgroundColor: primaryColor }]} onPress={handleEnable}>
        <ButtonText style={styles.buttonText}>{i18n.t('launchScreens.notifications.enableButton')}</ButtonText>
      </Button>
      <Button variant="link" style={styles.laterButton} onPress={onClose}>
        <ButtonText style={styles.laterButtonText}>{i18n.t('launchScreens.notifications.laterButton')}</ButtonText>
      </Button>
    </ModalBottom>
  )
}

const styles = StyleSheet.create({
  iconCircle: {
    width: ICON_CIRCLE_SIZE,
    height: ICON_CIRCLE_SIZE,
    borderRadius: ICON_CIRCLE_SIZE / 2,
    backgroundColor: theme.colors.lightGray,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.padding.x2,
  },
  title: {
    marginTop: theme.padding.x2,
    fontSize: theme.textSize.x2_50,
    fontWeight: 'bold',
    color: theme.colors.black,
  },
  subtitle: {
    marginTop: theme.padding.x05,
    fontSize: theme.textSize.x1_75,
    color: theme.colors.silverStrong,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.padding.x1,
    marginTop: theme.padding.x2,
  },
  chip: {
    paddingVertical: theme.padding.x05,
    paddingHorizontal: theme.padding.x1,
    borderRadius: CHIP_RADIUS,
  },
  chipText: {
    fontSize: theme.textSize.x1_75,
  },
  button: {
    marginTop: theme.padding.x3,
    borderRadius: BUTTON_RADIUS,
  },
  buttonText: {
    color: theme.colors.white,
  },
  laterButton: {
    marginTop: theme.padding.x05,
  },
  laterButtonText: {
    color: theme.colors.silverStrong,
  },
})
